// ===========================================
// Onboarding check-in React Query hooks (enquiries Phase 6)
// ===========================================
// A tick changes the check-in, and can change an enquiry: its "Onboarding overdue" flag, its
// Onboarding block, and (the school's switch on) its stage. So every tick refreshes the enquiry
// caches too.

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { apiErrorCode, apiErrorMessage } from '../utils/apiError';
import { settlingInApi, type SettlingInStatus } from '../api/settlingIn.api';
import { enquiryKeys, SETTLING_IN_KEY_ROOT } from './useEnquiries';
import { dashboardKeys } from './useDashboard';

export const settlingInKeys = {
  all: SETTLING_IN_KEY_ROOT,
  lists: () => [...settlingInKeys.all, 'list'] as const,
  list: (params: { status?: SettlingInStatus; page?: number } = {}) => [...settlingInKeys.lists(), params] as const,
  items: (opts: { includeHidden?: boolean } = {}) => [...settlingInKeys.all, 'items', opts] as const,
};

/**
 * Returned (and awaited by the mutation), so the tick stays pending until the list has refetched:
 * the controlled checkbox never flashes its old value (/qa F-L1).
 */
function invalidateAfterTick(queryClient: QueryClient): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: settlingInKeys.all }),
    queryClient.invalidateQueries({ queryKey: enquiryKeys.all }),
    queryClient.invalidateQueries({ queryKey: dashboardKeys.admin.all }),
  ]);
}

/**
 * `enabled` is the caller's `can('student_onboarding', 'read')`: never fetched without it.
 *
 * ⚠️ Always fresh (`staleTime: 0`, refetched on every mount, /qa F-M1): the rows are DERIVED from
 * what other screens change (an enrolment opens a check-in, the onboarding window moves every due
 * day, an enquiry's stage moves its chip), and the app-wide 5-minute cache would show the office a
 * stale work queue.
 */
export function useSettlingIns(params: { status?: SettlingInStatus; page?: number }, enabled: boolean) {
  return useQuery({
    queryKey: settlingInKeys.list(params),
    queryFn: () => settlingInApi.list(params),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/** The snackbar after a tick that moved the student's enquiries to Enrolled (/qa F-L7). */
export function enquiriesMovedMessage(moved: number): string {
  return moved === 1
    ? 'Checklist complete: the enquiry has moved to Enrolled'
    : `Checklist complete: ${moved} enquiries have moved to Enrolled`;
}

export function useTickSettlingInItem() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ studentId, itemId, done }: { studentId: string; itemId: string; done: boolean }) =>
      settlingInApi.tick(studentId, itemId, done),
    onSuccess: (result) => {
      if (result.enquiriesMoved > 0) {
        enqueueSnackbar(enquiriesMovedMessage(result.enquiriesMoved), { variant: 'success' });
      }
      return invalidateAfterTick(queryClient);
    },
    onError: (error: Error) => {
      enqueueSnackbar(apiErrorMessage(error, 'Failed to update the checklist'), { variant: 'error' });
      return invalidateAfterTick(queryClient);
    },
  });
}

/** The school's checklist items. `enabled` is `can('student_onboarding', 'read')`. */
export function useChecklistItems(opts: { includeHidden?: boolean }, enabled: boolean) {
  return useQuery({
    queryKey: settlingInKeys.items(opts),
    queryFn: () => settlingInApi.listItems(opts),
    enabled,
  });
}

function useItemMutation<TVars, TResult>(mutationFn: (vars: TVars) => Promise<TResult>, error: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const refresh = () => queryClient.invalidateQueries({ queryKey: [...settlingInKeys.all, 'items'] });
  return useMutation({
    mutationFn,
    // Returned: the mutation stays pending until the list has refetched, so a second reorder click
    // is computed from the NEW order, never the stale one (/qa F-L6).
    onSuccess: () => refresh(),
    onError: (e: Error) => {
      enqueueSnackbar(apiErrorMessage(e, error), { variant: 'error' });
      // "The list changed. Reload it": reload it, or the next click fails the same way.
      if (apiErrorCode(e) === 'OPTION_ORDER_STALE') return refresh();
      return undefined;
    },
  });
}

/** The owner tier only. Resolves to `{ option, existed }`. */
export function useAddChecklistItem() {
  return useItemMutation((label: string) => settlingInApi.addItem(label), 'Failed to add the item');
}

/** The owner tier only. */
export function useUpdateChecklistItem() {
  return useItemMutation(
    ({ optionId, patch }: { optionId: string; patch: { label?: string; isActive?: boolean } }) => settlingInApi.updateItem(optionId, patch),
    'Failed to update the item',
  );
}

/** The owner tier only. */
export function useReorderChecklistItems() {
  return useItemMutation((orderedIds: string[]) => settlingInApi.reorderItems(orderedIds), 'Failed to reorder the checklist');
}
